import { useEffect, useRef, useState } from "react";
import { Volume2, Pause, Play, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const VOICES = [
  { v: "onyx",    l: "Onyx",    desc: "Deep · stoner"     },
  { v: "echo",    l: "Echo",    desc: "Smooth · casual"   },
  { v: "fable",   l: "Fable",   desc: "British storyteller" },
  { v: "alloy",   l: "Alloy",   desc: "Balanced"          },
  { v: "nova",    l: "Nova",    desc: "Bright · upbeat"   },
  { v: "shimmer", l: "Shimmer", desc: "Cheerful"          },
];

/**
 * Audio playback button + voice picker for any text.
 * Caches one HTMLAudioElement; busts on text or voice change.
 */
export default function BudSpeak({ text, defaultVoice = "onyx", showPicker = true, compact = false }) {
  const [voice, setVoice] = useState(() => {
    try { return localStorage.getItem("xseed.bud_voice") || defaultVoice; } catch { return defaultVoice; }
  });
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const lastKey = useRef("");

  useEffect(() => () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (e) { /* ignore */ }
      audioRef.current = null;
    }
  }, []);

  const setVoicePref = (v) => {
    setVoice(v);
    try { localStorage.setItem("xseed.bud_voice", v); } catch (e) { /* ignore */ }
    // bust cached audio so next play uses new voice
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (e) { /* ignore */ }
      audioRef.current = null;
      lastKey.current = "";
      setPlaying(false);
    }
  };

  const play = async () => {
    if (!text || !text.trim()) return;
    if (audioRef.current && lastKey.current === `${voice}::${text}`) {
      if (playing) { audioRef.current.pause(); setPlaying(false); }
      else { audioRef.current.play(); setPlaying(true); }
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/tts", { text: text.slice(0, 3500), voice, model: "tts-1", speed: 0.95 });
      const audio = new Audio(`data:audio/mp3;base64,${data.audio_b64}`);
      audio.onended = () => setPlaying(false);
      audio.onpause = () => setPlaying(false);
      audio.onplay = () => setPlaying(true);
      audioRef.current = audio;
      lastKey.current = `${voice}::${text}`;
      await audio.play();
    } catch (e) {
      toast.error("Bud lost his voice");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        data-testid="bud-speak-btn"
        onClick={play}
        disabled={busy || !text}
        className={`btn-x ${playing ? "btn-primary" : "btn-ghost"} !py-2 !px-4 !text-xs disabled:opacity-50`}
        title={playing ? "Pause Bud" : "Hear Bud"}
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : playing ? <Pause size={12} strokeWidth={2.6} /> : <Play size={12} strokeWidth={2.6} />}
        {compact ? null : (busy ? "Loading…" : playing ? "Pause" : "Hear Bud")}
      </button>
      {showPicker && (
        <div className="flex items-center gap-1 flex-wrap">
          <Volume2 size={11} strokeWidth={2.6} className="opacity-60" />
          {VOICES.map((vc) => (
            <button
              key={vc.v}
              data-testid={`voice-${vc.v}`}
              onClick={() => setVoicePref(vc.v)}
              title={vc.desc}
              className={`text-[10px] font-mono-x font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border-2 border-[#0B0F0C] transition-all ${voice === vc.v ? "bg-[#FF1A8C] text-white" : "bg-[#FAF6E8] hover:bg-[#FFE74C]"}`}
            >
              {vc.l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
